import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { userGroupApi } from '@/api';
import { QUERY_KEY_CONST } from '@/constant';
import usePaginationSearchParams from '@/hook/tanstackQuery/usePaginationSearchParams';
import type { User, UserGroup, UserGroupApi } from '@/type';

export const useCreateUserGroupQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: UserGroupApi.Create.Request) => userGroupApi.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_GROUP_KEYS.getList(),
      });
    },
  });
};

export const useUserGroupListQuery = () => {
  const paginationSearchParams = usePaginationSearchParams('userGroup');

  const listParams: Partial<UserGroupApi.GetList.Request> = {
    ...paginationSearchParams,
  };

  Object.entries(listParams).forEach(([key, value]) => {
    if (value === null) delete listParams[key as keyof typeof listParams];
  });

  return useQuery({
    queryFn: () =>
      userGroupApi.getList(listParams).then(res => res.data.resultData),
    queryKey: QUERY_KEY_CONST.USER_GROUP_KEYS.getList(listParams),
  });
};

export const useMapUserQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userGroup: group,
      users,
    }: {
      userGroup: UserGroup;
      users: User[];
    }) => {
      const pGroupID = group.id;
      const params = Object.assign(users, {
        get pGroupID() {
          return pGroupID;
        },
      });

      return userGroupApi.mapUser(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_GROUP_KEYS.getList(),
      });
    },
  });
};

export const useUnMapUserQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      users,
      userGroup,
    }: {
      users: User[];
      userGroup: UserGroup;
    }) => {
      const pGroupID = userGroup.id;
      const params = Object.assign([...users], {
        get pGroupID() {
          return pGroupID;
        },
      });

      return userGroupApi.unMapUser(params);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_GROUP_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_KEYS.getList(),
      });
    },
  });
};

export const useRemoveUserGroupQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userGroupID: UserGroup['id']) =>
      userGroupApi.remove({ id: userGroupID }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_GROUP_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_KEYS.getList(),
      });
    },
  });
};
